import { TabStyles } from '@tableau/widgets';
import { NestedCSSProperties } from 'typestyle/lib/types';

import * as _ from 'underscore';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { imgPath, imgUrl } from '../../../common/utilities/ImgUrl';
import { classes, cssClass } from '../../../style';

export namespace PermissionsGridStyles {
  const actionDialogPrefix = 'ActionDialog';
  const granteeCellCssPrefix = 'GranteeCell';
  const permissionsGridCssPrefix = 'PermissionsGrid';
  const permissionRoleCellCssPrefix = 'PermissionRoleCell';
  const siteRoleCellCssPrefix = 'SiteRoleCell';
  const capabilitiesCellCssPrefix = 'CapabilitiesCell';
  const capabilitiesHeaderCssPrefix = 'CapabilitiesHeader';
  const disabledTextStyleCssPrefix = 'DisabledTextStyle';
  const noResultsTextCssPrefix = 'NoResultsTextStyle';

  const ButtonPanelLeftPadding = 7;
  // NOTE: ButtonPanelWidth = 
  // 2*(button width 46 + 2*(button padding 24 + button border 1)) 
  // + 12 middle margin between buttons
  // + 25 to accommodate long translated text (TFSID 1086270)
  const ButtonPanelWidth = 229;
  const ActionMenuWidth = 43;
  const DropdownArrowWidth = 18;
  const NoResultsTextFontSize = 18;
  const NoResultsTextTopPadding = 40;
  export const MaxRows = 6;
  export const RoleCellWidth = 100;
  export const EmptySpaceMargin = 24;
  export const RowHeight = 32;
  export const GranteeNameWidth = ButtonPanelWidth + ButtonPanelLeftPadding;
  export const GranteeCellWidth = GranteeNameWidth + ActionMenuWidth;
  export const SelectedRowBackgroundColor = '#BEDBF4';
  const DisabledTextColor = '#BFBFBF';
  export const ExplicitGranteeCellColor = '#FAFAFA';
  const GridBorderBottomColor = '#CBCBCB';
  const GranteeSearchFieldSpace = 4;
  const GranteeSearchFieldWidth = GranteeCellWidth - 25;
  const GranteeSearchFieldPopupWidth = GranteeSearchFieldWidth - 20;
  const GranteeSearchFieldPopupContentWidth = GranteeSearchFieldPopupWidth - 12;
  const GranteeSearchFieldPopupMaxHeight = 120;
  const GranteeSearchFieldPopupHeight = 113;
  export const GranteeSearchFieldPopupWhitespaceHeight = 14;
  export const GranteeSearchFieldPopupTotalHeight = GranteeSearchFieldSpace + GranteeSearchFieldPopupMaxHeight + GranteeSearchFieldPopupWhitespaceHeight;
  const GranteeSearchResultImgContainerWidth = 24;
  const GranteeSearchResultImgWidth = 14;
  const HoveredRowBackgroundColor = '#F5F5F5';
  export const HoverableGranteeCellClass = 'grantee-cell-hoverable';

  export const DisabledTextStyle = cssClass(disabledTextStyleCssPrefix, '', {
    color: DisabledTextColor,
  });

  export const NoResultsTextStyle = cssClass(noResultsTextCssPrefix, '', {
    color: `${CommonStyles.Colors.AccessibleSecondaryTextColor}`,
    fontSize: `${NoResultsTextFontSize}px`,
    paddingTop: `${NoResultsTextTopPadding}px`,
    textAlign: 'center',
  });

  export type DataGridContainerStyleArgs = {
    numRows: number; numAddedRows: number; lastRowInEditMode: boolean; inEditMode: boolean; buttonPanelIndex: number; searching: boolean; isExplicit: boolean; disabled: boolean;
  };

  export let memoizedDataGridContainer = _.memoize(DataGridContainer, (args) => { return JSON.stringify(args); });
  export function DataGridContainer(args: DataGridContainerStyleArgs): string {
    let borderBottom = `1px solid ${GridBorderBottomColor}`;
    let body: NestedCSSProperties = {
      top: `${RowHeight + 1}px !important`,
      backgroundColor: 'transparent !important',
      overflowX: 'hidden !important' as any, // disable horizontal scrolling
    };

    // if the last row is the row in edit mode, increase the number of rows of the grid by one.
    // this is because the button panel is actually part of the grid, not an overlay/popup,
    // so if the grid does not become taller then the button panel will be cutoff because of
    // overflow hidden (needed to disable scrolling)
    let maxRows = args.lastRowInEditMode ? MaxRows + 1 : MaxRows;
    const bodyHeight = args.searching ? RowHeight * (args.numRows - args.numAddedRows) + GranteeSearchFieldPopupTotalHeight : RowHeight * args.numRows;
    if (args.inEditMode) {
      borderBottom = `1px solid transparent`; // remove because in edit mode, the button panel could be below the last row, which would force the border lower
      body = {
        ...body,
        // the search box is also inside of the grid element, so need to increase height to account for it
        height: `${bodyHeight}px !important`,
        maxHeight: args.searching ? `${RowHeight * maxRows + 1 + GranteeSearchFieldPopupTotalHeight}px` : `${RowHeight * maxRows + 1}px`,
      };
    }

    let row: NestedCSSProperties = {};
    if (args.disabled) {
      body = {
        ...body,
        overflowY: 'hidden !important' as any, // can't scroll when disabled
      };
    } else {
      if (!args.isExplicit) {
        body = {
          ...body,
          cursor: 'pointer',
        };
      }
      row = {
        $nest: {
          '&:hover': {
            backgroundColor: `${HoveredRowBackgroundColor} !important`,
            $nest: {
              [`.${HoverableGranteeCellClass}`]: {
                backgroundColor: `${HoveredRowBackgroundColor} !important`,
              },
              '[class^="PermissionRoleCell_container"]': {
                backgroundColor: `${HoveredRowBackgroundColor} !important`,
              }
            }
          },
        },
      };
    }

    const height = args.inEditMode ? `${bodyHeight + RowHeight * (args.numAddedRows) + 2}px` : `${RowHeight * args.numRows + 1}px`;

    return cssClass(permissionsGridCssPrefix, 'container', {
      borderBottom,
      height,
      maxHeight: args.searching ? '' : `${RowHeight * (maxRows + 1) + 2}px`, // add one to maxRows to account for header row
      $nest: {
        '.tb-react-data-grid': {
          '-webkit-user-select': 'none', // safari
          '-moz-user-select': 'none', // firefox
          '-ms-user-select': 'none', // IE10+ and Edge
          userSelect: 'none', // standard, recognized by chrome and opera
          overflowY: 'visible !important' as any, // needed for IE to be able to show editable grantee cell button panel on last row
        },
        '.tb-react-dg-hcol': {
          borderBottom: 'none !important',
        },
        [`.tb-react-dg-hrow`]: {
          borderTop: 'none',
          borderBottomColor: GridBorderBottomColor,
        },
        '.tb-react-dg-brow': row,
        ['.tb-react-dg-bcell']: {
          outline: `none !important` // Override the data grid body cell outline
        },
        [`.tb-react-dg-body`]: body,
        '& [aria-selected="true"]': {
          backgroundColor: `${SelectedRowBackgroundColor} !important`,
        },
        '& [aria-colindex="1"]': { // grantee cell column
          minWidth: '242px !important', // grantee cell has to have min-width or else edit button panel looks wrong
          paddingLeft: '0px !important', // grantee cell doesn't have the default cell padding
          paddingRight: '0px !important', // grantee cell doesn't have the default cell padding
          top: `${RowHeight + 1}px !important`,
        },
        [`& [aria-colindex="1"][aria-rowindex="${args.buttonPanelIndex}"]`]: {
          height: `${args.searching ? (RowHeight + GranteeSearchFieldPopupMaxHeight + GranteeSearchFieldPopupWhitespaceHeight + GranteeSearchFieldSpace) : (RowHeight * 2)}px !important`,
          backgroundColor: 'transparent !important', // when adding a new row, the extra height behind the search results needs to be transparent, not selected color
          $nest: {
            '> div': {
              height: `${(RowHeight + GranteeSearchFieldPopupMaxHeight + GranteeSearchFieldPopupWhitespaceHeight + GranteeSearchFieldSpace)}px`,
            },
          },
        },
        [`&[aria-colindex="3"]`]: {
          paddingLeft: '0px !important',
          paddingRight: '0px !important'
        }
      }
    });
  }

  export const PermissionsGridContainerBase = cssClass(permissionsGridCssPrefix, 'grid-container', {
    position: 'relative',
  });

  export const ActionDialogText = cssClass(actionDialogPrefix, 'body', {
    color: '#333333',
  });


  export const GranteeIconProjectLeader = imgPath('permission_role_project_leader.svg');
  export const GranteeIconProjectLeaderDisabled = imgPath('permission_role_project_leader_disabled.svg');
  export const GranteeIconUser = imgPath('spaces_navigation_admin_people.svg');
  export const GranteeIconUserDisabled = imgPath('spaces_navigation_admin_people_disabled.svg');
  export const GranteeIconGroup = imgPath('spaces_navigation_admin_groups.svg');
  export const GranteeIconGroupDisabled = imgPath('spaces_navigation_admin_groups_disabled.svg');


  export const GranteeInfoCellContainer = cssClass(granteeCellCssPrefix, 'info-container', {
    display: 'flex',
    minWidth: '204px',
    overflow: 'hidden',
  });

  export const GranteeCellImgContainer = cssClass(granteeCellCssPrefix, 'img-container', {
    alignItems: 'center',
    display: 'inline-flex',
    height: `${RowHeight}px`,
    width: `${RowHeight}px`,
  });

  export const GranteeCellImg = cssClass(granteeCellCssPrefix, 'img-container', {
    margin: 'auto'
  });

  export const GranteeCellStripe = cssClass(granteeCellCssPrefix, 'stripe', {
    backgroundColor: `${CommonStyles.Colors.Grey102}`,
    height: `${RowHeight + 1}px`,
    position: 'relative',
    top: `-${RowHeight + 1}px`,
    width: '3px',
  });

  export const DataGridCellOverrideRemovePadding: React.CSSProperties = {
    paddingRight: 0,
  };

  const GranteeCellTextBase = cssClass(granteeCellCssPrefix, 'text', {
    marginLeft: '8px',
    marginRight: '24px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: `${GranteeNameWidth}px`,
  });

  export const GranteeCellTextColor = cssClass(granteeCellCssPrefix, 'text-color', {
    color: CommonStyles.Colors.Grey51,
  });

  export const GranteeCellTextColorDisabled = cssClass(granteeCellCssPrefix, 'text-color-disabled', {
    color: DisabledTextColor,
  });

  export const GranteeCellText = classes(GranteeCellTextBase, GranteeCellTextColor);
  export const GranteeCellTextDisabled = classes(GranteeCellTextBase, GranteeCellTextColorDisabled);

  export const GranteeCellContainerSelected = cssClass(granteeCellCssPrefix, 'container-selected', {
    backgroundColor: SelectedRowBackgroundColor,
  });

  export const GranteeCellContainerExplicit = cssClass(granteeCellCssPrefix, 'container-explicit', {
    backgroundColor: CommonStyles.Colors.Grey250,
    borderRight: `1px solid ${CommonStyles.Colors.Grey235}`,
  });

  export const GranteeCellContainerExplicitSelected = cssClass(granteeCellCssPrefix, 'container-explicit-selected', {
    backgroundColor: SelectedRowBackgroundColor,
    borderRight: `1px solid ${CommonStyles.Colors.Grey235}`,
  });

  const EditableGranteeCellContainerBase = cssClass(granteeCellCssPrefix, 'editable-container', {
    alignItems: 'center',
    borderRight: `1px solid ${CommonStyles.Colors.Grey235}`,
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'space-between',
    $nest: {
      'button[data-tb-test-id="action-menu-Button"]': {
        $nest: {
          '[class^="spaces-explorer_explorer-dropdown-button"]': {

            backgroundColor: 'transparent !important',
          }
        },
        backgroundColor: 'transparent !important',
      },
    },
  });

  const EditableGranteeCellContainerColors = cssClass(granteeCellCssPrefix, 'editable-container-colors', {
    backgroundColor: CommonStyles.Colors.Grey250,
    borderBottom: `1px solid ${CommonStyles.Colors.Grey235}`,
  });

  const EditableGranteeCellContainerColorsSelected = cssClass(granteeCellCssPrefix, 'editable-container-colors-selected', {
    backgroundColor: SelectedRowBackgroundColor,
    borderBottom: `1px solid ${SelectedRowBackgroundColor}`,
  });

  export const EditableGranteeCellContainer = classes(EditableGranteeCellContainerBase, EditableGranteeCellContainerColors);
  export const EditableGranteeCellContainerSelected = classes(EditableGranteeCellContainerBase, EditableGranteeCellContainerColorsSelected);

  export const EditableGranteeCellSearchField = cssClass(granteeCellCssPrefix, 'editable-search-field', {
    position: 'relative',
    height: `${RowHeight}px`,
    width: `${GranteeSearchFieldWidth}px`,
    $nest: {
      '& input': { backgroundColor: `${CommonStyles.Colors.White} !important` },
      '& [role="listbox"]': {
        backgroundColor: CommonStyles.Colors.White,
        border: `1px solid ${TabStyles.Colors.L6}`,
        margin: '1px 3px',
        maxHeight: `${GranteeSearchFieldPopupHeight}px`,
        position: 'absolute',
        top: '100%',
        width: `${GranteeSearchFieldPopupWidth}px`,
        zIndex: 2 // allow popup to show up above button panel
      },
      '&:not(.showPopup) [role="listbox"]': {
        display: 'none',
      },
    }
  });

  export const EditableGranteeCellSearchFieldContainer = cssClass(granteeCellCssPrefix, 'editable-search-field-container', {
    paddingLeft: '4px',
    paddingRight: '21px',
    height: `${RowHeight}px`
  });

  export const EditableGranteeCellSearchResultContainer = cssClass(granteeCellCssPrefix, 'editable-search-result-container', {
    overflow: 'visible',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${GranteeSearchFieldPopupContentWidth}px`,
  });

  export const EditableGranteeCellSearchResultImg = cssClass(granteeCellCssPrefix, 'editable-search-result-img', {
    height: `${GranteeSearchResultImgWidth}px`,
    width: `${GranteeSearchResultImgWidth}px`,
  });

  export const EditableGranteeCellSearchResultImgContainer = cssClass(granteeCellCssPrefix, 'editable-search-result-img-container', {
    alignItems: 'center',
    display: 'inline-flex',
    height: `${GranteeSearchResultImgContainerWidth}px`,
    verticalAlign: 'middle',
    width: `${GranteeSearchResultImgContainerWidth}px`,
  });

  export const EditableGranteeCellSubContainer = cssClass(granteeCellCssPrefix, 'editable-sub-container', {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    overflow: 'hidden',
    width: '211px',
  });

  export const EditableGranteeCellButtonPanel = cssClass(granteeCellCssPrefix, 'editable-button-panel', {
    backgroundColor: SelectedRowBackgroundColor,
    justifyContent: 'left',
    marginTop: '-2px',
    paddingLeft: '7px',
    position: 'absolute',
    width: `${ButtonPanelWidth + ActionMenuWidth - 2}px`,
  });

  export const EditableGranteeCellButton = cssClass(granteeCellCssPrefix, 'editable-button-panel-button', {
    lineHeight: '18px',
    $nest: {
      'img': {
        verticalAlign: 'middle', // loading spinner appears towards top of button without this
      },
    },
  });

  const EditableGranteeCellCancelButtonMargin = cssClass(granteeCellCssPrefix, 'editable-button-panel-cancel', {
    marginRight: '12px',
  });

  export const EditableGranteeCellCancelButton = classes(EditableGranteeCellCancelButtonMargin, EditableGranteeCellButton);

  export const SelectedPermissionRoleCellContainer = cssClass(permissionRoleCellCssPrefix, 'selected-container', {
    $nest: {
      'button:hover:enabled': {
        backgroundColor: SelectedRowBackgroundColor,
      },
    },
  });

  export const NotAllowedPermissionCellContainer = cssClass('container', 'not-allowed', {
    $nest: {
      ':hover': {
        cursor: 'not-allowed !important',
      },
    },
  });

  export const PermissionRoleCellContainer = cssClass(permissionRoleCellCssPrefix, 'container', {
    $nest: {
      '&>div': {
        display: 'initial !important',
        $nest: {
          'button[data-tb-test-id="permissions-grid-permission-role-Button"]': {
            $nest: {
              '[class^="spaces-explorer_explorer-dropdown-button"]': {
                backgroundColor: 'transparent !important',
              },
            },
            backgroundColor: 'transparent !important',
          },
        },
      },
      'button:hover:enabled': {
        cursor: 'pointer'
      },
    },
  });

  export const PermissionRoleCellSubContainerBase = cssClass(permissionRoleCellCssPrefix, 'subcontainer-base', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexGrow: 1,
  });

  const PermissionRoleCellSubContainerSelectedColor = cssClass(permissionRoleCellCssPrefix, 'subcontainer-selected', {
    backgroundColor: SelectedRowBackgroundColor,
  });

  export const PermissionsRoleCellSubContainerSelected = classes(PermissionRoleCellSubContainerBase, PermissionRoleCellSubContainerSelectedColor);

  export const PermissionRoleCellText = cssClass(siteRoleCellCssPrefix, 'text', {
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${RoleCellWidth - DropdownArrowWidth}px`,
  });

  export const SiteRoleCellText = cssClass(siteRoleCellCssPrefix, 'text', {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: `${RoleCellWidth}px`,
  });

  export const SiteRoleTextDisabled = classes(DisabledTextStyle, SiteRoleCellText);

  export const CapabilitiesCellSection = cssClass(capabilitiesCellCssPrefix, 'section', {
    marginLeft: '24px',
  });

  export const CapabilitiesCellCapabilityContainerBase = cssClass(capabilitiesCellCssPrefix, 'capability-container', {
    width: '32px',
    $nest: {
      '&>div': {
        display: 'inline-flex',
        height: '14px',
        verticalAlign: 'middle',
      }
    }
  });

  const CapabilitiesCellCapabilityContainerLeftMargin = cssClass(capabilitiesCellCssPrefix, 'capability-container-left-margin', {
    marginLeft: '11px',
  });

  export const CapabilitiesCellCapabilityContainerWithLeftMargin = classes(CapabilitiesCellCapabilityContainerBase, CapabilitiesCellCapabilityContainerLeftMargin);

  export const CapabilitiesCellCapability = cssClass(capabilitiesCellCssPrefix, 'capability', {
    display: 'inline-flex',
    width: '32px',
  });

  export const EditableCapabilitiesCellCapability = cssClass(capabilitiesCellCssPrefix, 'editable-capability', {
    display: 'inline-flex',
    width: '32px',
    border: `1px solid ${CommonStyles.Colors.Grey51}`,
    boxSizing: 'border-box',
  });

  export const HoverableCapabilitiesCellContainer = cssClass(capabilitiesCellCssPrefix, 'hoverable-capability', {
    $nest: {
      '&:hover': {
        border: `1px solid ${CommonStyles.Colors.Grey51}`,
        boxSizing: 'border-box',
      },
    },
  });

  export const EffectiveAllow = imgPath('server_viz_portal_effective_permission_allowed_green.svg');
  export const EffectiveAllowDisabled = imgPath('server_viz_portal_effective_permission_allowed_disabled.svg');
  export const EffectiveMixed = imgPath('server_viz_portal_effective_permission_mixed_gray.svg');
  export const EffectiveMixedDisabled = imgPath('server_viz_portal_effective_permission_mixed_disabled.svg');
  export const EffectiveDenied = imgPath('server_viz_portal_effective_permission_denied_red.svg');
  export const EffectiveDeniedDisabled = imgPath('server_viz_portal_effective_permission_denied_disabled.svg');

  export const ExplicitAllow = imgPath('server_viz_portal_permission_rule_allowed_white.svg');
  export const ExplicitMixed = imgPath('server_viz_portal_permission_rule_mixed_white.svg');
  export const ExplicitDenied = imgPath('server_viz_portal_permission_rule_denied_white.svg');

  export const CapabilitiesCellImg = cssClass(capabilitiesCellCssPrefix, 'img', {
    height: '8px',
    margin: 'auto',
  });

  export const CapabilitiesCellImgMixed = cssClass(capabilitiesCellCssPrefix, 'img-mixed', {
    height: '2px',
    margin: 'auto',
  });

  export const CapabilitiesCellAllowedCapability = cssClass(capabilitiesCellCssPrefix, 'allowed', {
    backgroundColor: TabStyles.Colors.GoLight,
  });

  export const CapabilitiesCellAllowedCapabilityDisabled = cssClass(capabilitiesCellCssPrefix, 'allowed-disabled', {
    backgroundColor: '#ABEAD5',
  });

  export const CapabilitiesCellDeniedCapability = cssClass(capabilitiesCellCssPrefix, 'denied', {
    backgroundColor: '#E51F25',
  });

  export const CapabilitiesCellDeniedCapabilityDisabled = cssClass(capabilitiesCellCssPrefix, 'denied-disabled', {
    backgroundColor: '#F4A5AB',
  });

  export const CapabilitiesCellMixedCapability = cssClass(capabilitiesCellCssPrefix, 'mixed', {
    backgroundColor: CommonStyles.Colors.Grey203,
  });

  export const CapabilitiesCellMixedCapabilityDisabled = cssClass(capabilitiesCellCssPrefix, 'mixed-disabled', {
    backgroundColor: CommonStyles.Colors.Grey235,
  });

  export const CapabilitiesCellUnspecifiedCapability = cssClass(capabilitiesCellCssPrefix, 'unspecified', {
    backgroundColor: CommonStyles.Colors.Grey230,
  });

  export const CapabilitiesCellUnspecifiedCapabilityDisabled = cssClass(capabilitiesCellCssPrefix, 'unspecified-disabled', {
    backgroundColor: '#F5F5F5',
  });

  export const EditableCapabilitiesCellUnspecifiedCapability = cssClass(capabilitiesCellCssPrefix, 'editable-unspecified', {
    backgroundColor: CommonStyles.Colors.White,
  });

  export const CapabilitiesHeader = cssClass(capabilitiesHeaderCssPrefix, '', {
    alignItems: 'center',
    display: 'inline-flex',
    height: `${RowHeight}px`,
    marginLeft: '12px'
  });

  export const CapabilitiesHeaderSection = cssClass(capabilitiesHeaderCssPrefix, 'section', {
    display: 'inline-flex',
    marginBottom: '6px',
  });

  export const CapabilitiesHeaderSectionLeftDivider = cssClass(capabilitiesHeaderCssPrefix, 'section-left-divider', {
    borderLeft: `1px solid #D4D4D4`,
    marginLeft: '15px',
    paddingLeft: '12px',
  });

  export const CapabilitiesHeaderSectionWithLeftDivider = classes(CapabilitiesHeaderSection, CapabilitiesHeaderSectionLeftDivider);

  export const CapabilitiesHeaderIconContainerBase = cssClass(capabilitiesHeaderCssPrefix, 'icon-container', {
    display: 'inline-flex',
  });

  export const CapabilitiesHeaderIconContainerNormalLeftMargin = cssClass(capabilitiesHeaderCssPrefix, 'icon-container-normal-left-margin', {
    marginLeft: '19px',
  });

  export const CapabilitiesHeaderIconContainerSmallLeftMargin = cssClass(capabilitiesHeaderCssPrefix, 'icon-container-small-left-margin', {
    marginLeft: '4px',
  });

  export const CapabilitiesHeaderCapabilityIcon = cssClass(capabilitiesHeaderCssPrefix, 'capability-icon', {
    height: `${EmptySpaceMargin}px`,
    width: `${EmptySpaceMargin}px`,
  });

  export const CapabilitiesHeaderIconContainerFirst = classes(CapabilitiesHeaderIconContainerBase, CapabilitiesHeaderIconContainerSmallLeftMargin);
  export const CapabilitiesHeaderIconContainer = classes(CapabilitiesHeaderIconContainerBase, CapabilitiesHeaderIconContainerNormalLeftMargin);
}
